/**
 * PDP "Try on" — reveals the theme's own CTA and forwards it to Tangiblee.
 *
 * Tangiblee ships as an async bundle that injects `.tangiblee-cta-wrapper` into
 * the gallery some unpredictable time after page load, and only for products it
 * can actually render. That injection is therefore the vendor's own eligibility
 * signal, and it is the one this script waits for: the button is rendered
 * `hidden` by pdp-tryon-button.liquid and is only unhidden once the vendor node
 * exists. A product Tangiblee cannot handle never gets a button, so there is no
 * dead CTA to click.
 *
 * The vendor node is hidden by _tryon.scss and otherwise left completely alone —
 * not moved, not cloned, not re-classed, not re-labelled. Forwarding a click to
 * it runs the SDK's own handler and opens the SDK's own modal, which is the same
 * technique the theme already uses for Okendo's hidden controls. That keeps the
 * SDK's DOM entirely the SDK's business, so its re-renders have nothing of ours
 * to fight with.
 */
(function () {
  'use strict';

  /* The snippet is rendered once per breakpoint container and each copy carries
     its own script tag, so guard against running twice. */
  if (window.__pdpTryOnLoaded) return;
  window.__pdpTryOnLoaded = true;

  var BUTTON_SELECTOR = '[data-pdp-tryon]';

  /* Must stay in step with the hide rule in _tryon.scss: the node we click has
     to be the node we hid, or a live vendor button appears beside ours. Scoped
     to the product wrapper rather than to the gallery element inside it —
     `.pdp-layout-v2` holds the product section and nothing else, so this still
     ignores the recommendation carousels' own mounts, while a CTA the SDK
     re-parents or duplicates within the product stays both hidden and
     clickable. */
  var HOST_SELECTOR = '.pdp-layout-v2';
  var CTA_SELECTOR = HOST_SELECTOR + ' .tangiblee-cta-wrapper';

  var observer = null;
  var frame = 0;
  var syncing = false;

  function ctaNode() {
    return document.querySelector(CTA_SELECTOR);
  }

  /**
   * Match every button's visibility to whether the vendor CTA exists.
   *
   * Idempotent by construction: a button already in the right state is skipped,
   * so a settled page does no DOM writes at all. That is what makes it safe to
   * drive from a MutationObserver — and the observer watches childList only,
   * while this only ever writes an attribute, so it cannot feed itself either.
   */
  function sync() {
    if (syncing) return;
    syncing = true;
    try {
      var hide = !ctaNode();
      var buttons = document.querySelectorAll(BUTTON_SELECTOR);
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].hidden === hide) continue;
        buttons[i].hidden = hide;
      }
    } finally {
      syncing = false;
    }
  }

  /* The SDK bursts mutations during init and again on variant changes; slick
     mutates the same subtree on every slide. Collapse all of it to at most one
     sync per frame. */
  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(function () {
      frame = 0;
      sync();
    });
  }

  /* Safe to call repeatedly and from any point in the lifecycle. */
  function stop() {
    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    /* Settle on the final state, in case a mutation landed in the frame we just
       cancelled. */
    sync();
  }

  function forward(event) {
    var button = event.target.closest ? event.target.closest(BUTTON_SELECTOR) : null;
    if (!button) return;

    var cta = ctaNode();
    if (!cta) return;

    event.preventDefault();
    cta.click();
  }

  function init() {
    if (!document.querySelector(BUTTON_SELECTOR)) return;

    /* Delegated, so it survives the gallery being re-rendered underneath us. */
    document.addEventListener('click', forward);

    /* The bundle may have won the race and injected already. */
    sync();

    if (!window.MutationObserver) return;

    /* Scoped to the product wrapper, so the callback is not woken by unrelated
       page activity. Falls back to the body if the wrapper is not on the page.

       Never disconnected. It used to be released 30s after load, on the theory
       that the SDK only injects once; it does not. It re-injects on every
       variant change and on a debounced resize (see the layout guard in
       pdp/_gallery.scss), and it withdraws the CTA when a selection is not
       renderable. Both directions matter: a withdrawal we stop watching for
       leaves a pill that opens nothing, and a late injection we stop watching
       for loses try-on for the rest of the session — the AB variation's author
       hit exactly that with a 10s cap and ended up watching forever
       (ab-figma-usp.js L2189-2216).

       The cost of keeping it is one `querySelector` plus one
       `querySelectorAll` per frame in which the product subtree changed at
       all, and `sync()` writes nothing once the state matches, so a settled
       page does no work. */
    var root = document.querySelector(HOST_SELECTOR) || document.body;

    observer = new window.MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });

    window.addEventListener('pagehide', stop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
